import React from "react";
import { ConfirmationModal } from "@packsize/components";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useIntl } from "react-intl";
import { IconButton } from "@mui/material";
import { useToast } from "@packsize/toast";
import { useToken } from "@packsize/authentication";

import { type ExternalAccessSubscription } from "../types";

import { ApiKeyInfoView } from "./connection-info/api-key-info-view";
import { OAuthInfoView } from "./connection-info/oauth-info-view";

interface ApiKeyInfoButtonProps {
	readonly apiKey: ExternalAccessSubscription;
}

export const ApiKeyInfoButton: React.FC<ApiKeyInfoButtonProps> = ({ apiKey }) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const { decodedToken } = useToken();
	const tenantId = decodedToken?.tid ?? "";
	const { toastSuccess } = useToast();
	const intl = useIntl();

	const handleCopyToClipboard = async (value: string): Promise<void> => {
		await navigator.clipboard.writeText(value);
		toastSuccess({ message: intl.formatMessage({ id: "Copied to clipboard", defaultMessage: "Copied to clipboard" }) });
	};

	const handleClose = () => setIsOpen(false);

	return (
		<>
			<ConfirmationModal
				open={isOpen}
				onClose={handleClose}
				onCancel={handleClose}
				onConfirm={handleClose}
				title={intl.formatMessage({ id: "Connection Details", defaultMessage: "Connection Details" })}
				confirmText=""
				cancelText={intl.formatMessage({ id: "Close", defaultMessage: "Close" })}
				size="sm"
				slotProps={{
					confirmButton: { sx: { display: "none" } },
					cancelButton: { variant: "contained" },
				}}
			>
				{apiKey.$type === "ApiGatewayOAuthMeta" ? (
					<OAuthInfoView
						name={apiKey.properties.displayName}
						clientId={apiKey.properties.clientId}
						scope={apiKey.properties.scope}
						onCopyToClipboard={handleCopyToClipboard}
					/>
				) : (
					<ApiKeyInfoView
						name={apiKey.properties.displayName}
						tenantId={tenantId}
						scope={apiKey.properties.scope}
						onCopyToClipboard={handleCopyToClipboard}
					/>
				)}
			</ConfirmationModal>
			<IconButton onClick={() => setIsOpen(true)} data-pendoid="api-key-info-button__button__open-modal">
				<InfoOutlinedIcon />
			</IconButton>
		</>
	);
};
